import React, { useEffect, useState, Suspense, lazy } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { ENV } from "./config";
import {
  // BrowserRouter as Router,
  HashRouter as Router,
  Routes,
  Route,
  HashRouter,
  // BrowserRouter,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "./layouts/Layout";
import {
  SetGlobalValues,
  setDefaultStoreId,
  reset,
  setToken,
  setAuthenticatedStore,
} from "./store/rootStore";
import { Spinner } from "./components/Spinner";
import { decrypt } from "./utility";
import FaroRouteTracker from "./observability/FaroRouteTracker";

const NotFound = lazy(() => import("./pages/NotFound/index"));
const Inventory = lazy(() => import("./pages/Inventory/index"));
const Purchase = lazy(() => import("./pages/Purchase/index"));
const Imports = lazy(() => import("./pages/Imports/index"));
const OutgoingTransfer = lazy(() => import("./pages/Transfer/index"));
const IncomingTransfer = lazy(() => import("./pages/Transfer/index"));
const StockAnalysis = lazy(() => import("./pages/StockAnalysis/index"));
const StockMovement = lazy(() => import("./pages/StockMovement/index"));
const StockReorder = lazy(() => import("./pages/StockReorder/index"));
const OutgoingOrderList = lazy(() => import("./pages/OutgoingOrder/index"));
const IncomingOrder = lazy(() => import("./pages/SellingLeads/index"));
const Warehouse = lazy(() => import("./pages/Warehouse/index"));
const WarehouseInventory = lazy(() => import("./pages/WarehouseInventory/index"));
const Zone = lazy(() => import("./pages/Zone/index"));
const Aisle = lazy(() => import("./pages/Aisle/index"));
const Shelf = lazy(() => import("./pages/Shelf/index"));
const Bin = lazy(() => import("./pages/Bin/index"));

const App = () => {
  const { keycloak, initialized } = useKeycloak();
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const getId = useSelector((state) => state.rootStore.storeId);
  const paramsId = useSelector((state) => state.rootStore.paramsId);
  const params = new URLSearchParams(window.location.search);
  const integrateKra = useSelector(
    (state) => state.rootStore.userProfileValues?.integrateKra
  );
  const encryptedStore = params.get("store");

  useEffect(() => {
    if (!initialized) {
      return; // Wait for Keycloak to initialize
    }

    if (!keycloak.authenticated) {
      keycloak.login();
      return;
    }

    let tokenRefreshInterval;

    const fetchProtectedData = async () => {
      try {

        console.log("protected api is called")
        let storeId = "";
        let url = `${ENV.REACT_APP_CENTRAL_AUTH}/api/protected`;
        if (encryptedStore) {
          const decoded = decodeURIComponent(encryptedStore);
          storeId = await decrypt(decoded); // ✅ wait for decryption
          url += `?store_id=${storeId}`;
        }
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.statusText}`
          );
        }

        const data = await response.json();
        if (data.status === "000") {
          dispatch(setAuthenticatedStore(data.info));
          dispatch(setDefaultStoreId(storeId));
          dispatch(setToken(keycloak));
          dispatch(SetGlobalValues());
          //         setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching protected data:", error);
        if (error.message.includes("401")) {
          keycloak.logout({
            redirectUri: `${ENV.HOME_URL}`,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProtectedData();

    // Refresh token every 5 minutes
    tokenRefreshInterval = setInterval(() => {
      keycloak
        .updateToken(300)
        .then((refreshed) => {
          if (refreshed) {
            console.log("Token refreshed");
          } else {
            console.log("Token still valid");
          }
        })
        .catch(() => {
          // Never call logout() here: it ends the shared SSO session and signs the
          // POS out too. keycloak-js clears the token only when Keycloak rejects the
          // refresh (400) — then re-login; otherwise keep the session and retry next tick.
          if (!keycloak.token) {
            console.error("Token refresh rejected; re-authenticating");
            keycloak.login();
          } else {
            console.error("Token refresh failed; will retry");
          }
        });
    }, 300000); // 5 minutes

    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
  }, [keycloak, initialized]);


  return keycloak.authenticated && !loading ? (
    <HashRouter>
      <FaroRouteTracker />
      <React.Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            {/* <Route index element={<Purchase />} /> */}
            <Route
              index
              element={
                integrateKra === "Yes" && ENV.REACT_APP_ENVIRONMENT !== "production" ? <Purchase /> : <StockAnalysis />
              }
            />

            {/* production routes */}
              <Route path="*" element={<NotFound />} />
              <Route path="/incoming-transfer" element={<IncomingTransfer />} />
              <Route path="/outgoing-transfer" element={<OutgoingTransfer />} />
              <Route path="/stock-analysis" index element={<StockAnalysis />} />
              <Route path="/stock-movement" element={<StockMovement />} />
              <Route path="/buying-leads" element={<OutgoingOrderList />} />
              <Route path="/selling-leads" element={<IncomingOrder />} />
            {/* end of production routes */}

            {/* staging routes */}
            {ENV.REACT_APP_ENVIRONMENT !== "production" && (
              <>
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/imports" element={<Imports />} />
                <Route path="/warehouse" element={<Warehouse />} />
                <Route path="/warehouse-inventory" element={<WarehouseInventory />} />
                <Route path="/zone" element={<Zone />} />
                <Route path="/aisle" element={<Aisle />} />
                <Route path="/shelf" element={<Shelf />} />
                <Route path="/bin" element={<Bin />} />
                {/* <Route path="/stock-reorder" element={<StockReorder />} /> */}
              </>
            )}
            {/* end of staging routes */}
          </Route>
        </Routes>
      </React.Suspense>
    </HashRouter>
  ) : (
    <Spinner />
  );
};

export default App;
